<script>
import { mapGetters, mapMutations } from "vuex";
import { textoFiltro } from "@/helpers/apuracaoCapex";
import AsyncExportButton from "@/components/async-export-button.vue";
import { apuracaoColaborador as api } from "@/api/apuracaocapex";
import { ITEMS_PER_PAGE_OPTIONS_SERVE_SIDE } from "@/helpers/itemsPerPageOptions";

/** Espera (ms) antes de buscar, depois que o usuário para de digitar. */
const ESPERA_DIGITACAO = 800;

/** Rótulo da fase na abertura "Alocação por projeto e fase". */
const ROTULO_FASE = { pesquisa: "Pesquisa", desenvolvimento: "Desenvolvimento", semFase: "Sem fase" };

export default {
  name: "apuracao-capex-apuracao-por-colaborador",
  components: { AsyncExportButton },
  computed: {
    ...mapGetters(["clientId", "anoBase", "getServerSidePaginationItemsPerPage"]),

    anoEfetivo: function () {
      return this.anoBase ? String(this.anoBase) : String(new Date().getFullYear());
    },

    /** Anos do filtro de ano-base (o atual e os cinco anteriores). */
    anosOptions: function () {
      const atual = Number(new Date().getFullYear());
      const anos = [];

      for (let ano = atual; ano > atual - 6; ano--) anos.push(String(ano));

      return anos;
    },

    beneficios: function () {
      return api.BENEFICIOS || [];
    },

    /**
     * Opções de "registros por página": a lista é paginada na API, então não existe "Todos" (-1).
     * O valor válido atual entra na lista para o rodapé receber o número, não o objeto da opção.
     */
    itemsPerPageOptions: function () {
      const opcoes = ITEMS_PER_PAGE_OPTIONS_SERVE_SIDE.filter((valor) => valor > 0);
      const atuais = this.perPaginaValida();

      if (opcoes.indexOf(atuais) < 0) opcoes.push(atuais);

      return opcoes.sort((a, b) => a - b);
    },

    /** A competência exibida está na ponta do ano-base? (desabilita a seta) */
    ehPrimeiraCompetencia: function () {
      return String(this.filtroCompetencia).slice(0, 2) === "01";
    },

    ehUltimaCompetencia: function () {
      return String(this.filtroCompetencia).slice(0, 2) === "12";
    },

    /** Competência no formato da API (`AAAA-MM`) — usada na chamada e na exportação. */
    competenciaApi: function () {
      return api.competenciaParaApi(this.filtroCompetencia);
    },

    /** Cartões de resumo da competência (custo e horas). */
    cartoes: function () {
      const totais = this.totais;

      return [
        {
          chave: "custo",
          titulo: "Custo do mês",
          valor: totais.custo,
          secundario: `${this.formatarHoras(totais.horas)} h trabalhadas`,
          classe: "cartao-total",
        },
        {
          chave: "pesquisa",
          titulo: "Pesquisa (despesa)",
          valor: totais.custoPesquisa,
          percentual: this.percentual(totais.custoPesquisa, totais.custo),
          secundario: `${this.formatarHoras(totais.horasPesquisa)} h`,
          classe: "cartao-pesquisa",
        },
        {
          chave: "desenvolvimento",
          titulo: "Desenvolvimento (ativo)",
          valor: totais.custoDesenvolvimento,
          percentual: this.percentual(totais.custoDesenvolvimento, totais.custo),
          secundario: `${this.formatarHoras(totais.horasDesenvolvimento)} h`,
          classe: "cartao-desenvolvimento",
        },
        {
          chave: "semFase",
          titulo: "Sem fase",
          valor: totais.custoSemFase,
          percentual: this.percentual(totais.custoSemFase, totais.custo),
          secundario: `${this.formatarHoras(totais.horasSemFase)} h`,
          classe: "cartao-sem-fase",
        },
      ];
    },

    /**
     * Cabeçalhos — o valor e o % convivem na mesma célula. A API ordena só por `colaborador`
     * e `matricula`; as demais colunas ficam sem ordenação (`sortable: false`).
     */
    headers: function () {
      const colunas = [
        { text: "Colaborador", value: "colaborador" },
        { text: "Horas", value: "horas", align: "end", width: "110px", nowrapHeader: false },
        { text: "Custo do mês", value: "custo", align: "end", width: "150px", nowrapHeader: false },
        { text: "Pesquisa", value: "custoPesquisa", align: "end", width: "170px", nowrapHeader: false },
        { text: "Desenvolvimento", value: "custoDesenvolvimento", align: "end", width: "180px", nowrapHeader: false },
        { text: "Sem fase", value: "custoSemFase", align: "end", width: "170px", nowrapHeader: false },
      ];

      return colunas.map((coluna) => ({
        ...coluna,
        sortable: coluna.sortable === undefined ? api.ehOrdenavel(coluna.value) : coluna.sortable,
      }));
    },

    /** Há dados em tela? (sem isso a linha de totais não aparece: vazio/carregando/erro) */
    temDados: function () {
      return !this.loading && this.rows.length > 0;
    },

    /** A tabela mostra só uma **página** dos registros? (então o total exibido é da página) */
    totalDaPagina: function () {
      return this.totalRegistros > this.rows.length;
    },

    /** Rótulo da linha fixa de total: `Total • Benefício • MM/AAAA` (+ `página atual`). */
    textoTotalGeral: function () {
      const beneficio = this.beneficios.find((item) => item.value === this.filtroBeneficio);
      const pagina = this.totalDaPagina ? " • página atual" : "";

      return `Total • ${beneficio ? beneficio.text : ""} • ${this.filtroCompetencia}${pagina}`;
    },
  },
  data: function () {
    return {
      loading: false,
      rows: [],
      expandidos: [],
      totalRegistros: 0,
      totais: {
        horas: 0,
        horasPesquisa: 0,
        horasDesenvolvimento: 0,
        horasSemFase: 0,
        custo: 0,
        custoPesquisa: 0,
        custoDesenvolvimento: 0,
        custoSemFase: 0,
      },
      opcoes: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      filtroAno: String(new Date().getFullYear()),
      filtroBeneficio: 1,
      filtroCompetencia: "",
      filtroColaborador: "",
      ordenacao: [],
      pagina: 1,
      porPagina: 10,
      ultimaAssinatura: "",
      timerBusca: null,
    };
  },
  created: function () {
    this.filtroAno = this.anoEfetivo;
    this.filtroCompetencia = `01/${this.filtroAno}`;
    this.porPagina = this.perPaginaValida(this.getServerSidePaginationItemsPerPage);
    this.opcoes = { page: 1, itemsPerPage: this.porPagina, sortBy: [], sortDesc: [] };
  },
  watch: {
    clientId: function () {
      this.carregar({ page: 1 });
    },
    filtroBeneficio: function () {
      this.buscarSeMudou();
    },
    filtroCompetencia: function () {
      this.buscarSeMudou();
    },
    filtroColaborador: function () {
      this.agendarBusca();
    },
  },
  beforeDestroy() {
    clearTimeout(this.timerBusca);
  },
  methods: {
    ...mapMutations(["setAnoBase", "setPaginationItemsPerPage"]),

    /** Busca a página na API (o overlay global acompanha a chamada). */
    carregar: function (params) {
      const opcoes = params || {};

      this.ultimaAssinatura = this.assinaturaFiltros();
      this.loading = true;

      api
        .listarApuracaoColaborador({
          empresaId: this.clientId,
          anoBase: this.filtroAno || this.anoEfetivo,
          competencia: this.filtroCompetencia,
          beneficio: this.filtroBeneficio,
          page: opcoes.page || this.pagina,
          perPage: this.perPaginaValida(opcoes.perPage || this.porPagina),
          sort: opcoes.sort || this.ordenacao,
          filtros: { colaborador: textoFiltro(this.filtroColaborador) },
        })
        .then((resultado) => {
          this.loading = false;
          this.rows = resultado.rows || [];
          this.totalRegistros = Number(resultado.total) || 0;
          this.pagina = Number(resultado.page) || 1;
          this.porPagina = Number(resultado.perPage) || this.porPagina;
          this.totais = this.somarTotais(this.rows);

          const respondido = { ...this.opcoes, page: this.pagina, itemsPerPage: this.porPagina };

          if (!this.mesmasOpcoes(respondido)) this.opcoes = respondido;

          this.recarregarDetalhesExpandidos();
        })
        .catch((erro) => {
          this.loading = false;
          this.rows = [];
          this.totalRegistros = 0;
          this.totais = this.totaisVazios();
          this.notify(erro);

          if (!erro.response) console.error(erro);
        });
    },

    /** Assinatura dos filtros efetivos (texto com `trim`) — evita chamada repetida. */
    assinaturaFiltros: function () {
      return [
        this.filtroAno,
        this.filtroBeneficio,
        this.filtroCompetencia,
        textoFiltro(this.filtroColaborador),
      ].join("|");
    },

    /** Busca só quando o filtro efetivo mudou (evita chamadas iguais em sequência). */
    buscarSeMudou: function () {
      if (this.assinaturaFiltros() === this.ultimaAssinatura) return;

      this.carregar({ page: 1 });
    },

    /** Totais com tudo zerado (estado inicial e depois de um erro). */
    totaisVazios: function () {
      return {
        horas: 0,
        horasPesquisa: 0,
        horasDesenvolvimento: 0,
        horasSemFase: 0,
        custo: 0,
        custoPesquisa: 0,
        custoDesenvolvimento: 0,
        custoSemFase: 0,
      };
    },

    /** Soma os totais das linhas em tela (a listagem da API é paginada). */
    somarTotais: function (linhas) {
      return (linhas || []).reduce((acumulado, linha) => ({
        horas: acumulado.horas + linha.horas,
        horasPesquisa: acumulado.horasPesquisa + linha.horasPesquisa,
        horasDesenvolvimento: acumulado.horasDesenvolvimento + linha.horasDesenvolvimento,
        horasSemFase: acumulado.horasSemFase + linha.horasSemFase,
        custo: acumulado.custo + linha.custo,
        custoPesquisa: acumulado.custoPesquisa + linha.custoPesquisa,
        custoDesenvolvimento: acumulado.custoDesenvolvimento + linha.custoDesenvolvimento,
        custoSemFase: acumulado.custoSemFase + linha.custoSemFase,
      }), this.totaisVazios());
    },

    /**
     * Recarrega a folha de quem está com a linha expandida: ao trocar competência/ano-base/
     * benefício as linhas viram objetos novos e o detalhe antigo pertenceria ao filtro anterior.
     * Lê só os ids e não reassinala `expandidos` (objetos comparados a ids recolheriam a linha).
     */
    recarregarDetalhesExpandidos: function () {
      const ids = this.rows.map((linha) => linha.id);
      const expandidos = (this.expandidos || [])
        .map((item) => (item && item.id !== undefined ? item.id : item))
        .filter((id) => ids.indexOf(id) >= 0);

      this.rows.forEach((linha) => {
        if (expandidos.indexOf(linha.id) >= 0) this.carregarDetalhe(linha);
      });
    },

    /** Carrega a folha do colaborador (linha expandida): **sob demanda** e uma vez por linha. */
    carregarDetalhe: async function (item) {
      if (item.detalhe || item.carregandoDetalhe) return;

      this.$set(item, "carregandoDetalhe", true);

      try {
        const folha = await api.obterFolhaColaborador({
          empresaId: this.clientId,
          anoBase: this.filtroAno || this.anoEfetivo,
          competencia: this.filtroCompetencia,
          beneficio: this.filtroBeneficio,
          funcionarioId: item.id,
        });

        this.$set(item, "detalhe", folha);
      } catch (erro) {
        this.notify(erro);

        if (!erro.response) console.error(erro);
      } finally {
        this.$set(item, "carregandoDetalhe", false);
      }
    },

    /** Botão de expandir: abre/fecha a linha e busca a folha na **primeira** abertura. */
    alternarDetalhe: function (item, expandido, alternar, evento) {
      alternar(!expandido, evento);

      if (!expandido) this.carregarDetalhe(item);
    },

    /** Agenda a busca por texto (só dispara quando o usuário para de digitar). */
    agendarBusca: function () {
      clearTimeout(this.timerBusca);

      this.timerBusca = setTimeout(() => {
        this.timerBusca = null;
        this.buscarSeMudou();
      }, ESPERA_DIGITACAO);
    },

    /** Troca do ano-base: a competência volta para janeiro do novo ano. */
    trocarAno: function () {
      this.setAnoBase(this.filtroAno);
      this.filtroCompetencia = `01/${this.filtroAno}`;
      this.carregar({ page: 1 });
    },

    /** Navegação entre competências (dentro do ano-base). */
    mudarCompetencia: function (passo) {
      this.filtroCompetencia = api.deslocarCompetencia(this.filtroCompetencia, passo, this.filtroAno);
    },

    /** Opções do `v-data-table`: a lista é paginada na API, então toda mudança vale nova busca. */
    onChangePage: function (options) {
      const pagina = Number(options.page) || 1;
      const porPagina = this.perPaginaValida(options.itemsPerPage);
      const sort = this.normalizarSort(options);
      const proximas = { ...options, page: pagina, itemsPerPage: porPagina };

      if (!this.mesmasOpcoes(proximas)) this.opcoes = proximas;

      this.setPaginationItemsPerPage(porPagina);

      if (this.mudouBusca(pagina, porPagina, sort)) {
        this.ordenacao = sort;
        this.carregar({ page: pagina, perPage: porPagina, sort });
      }
    },

    /** Ordenação do v-data-table no formato usado pela busca (`[{ field, desc }]`). */
    normalizarSort: function (options) {
      const sortBy = options.sortBy || [];
      const sortDesc = options.sortDesc || [];

      return sortBy.map((campo, indice) => ({ field: campo, desc: !!sortDesc[indice] }));
    },

    /** "Registros por página" válido para a API (nunca `per_page=-1`). */
    perPaginaValida: function (valor) {
      const numero = Number(valor !== undefined ? valor : this.porPagina);

      return numero > 0 ? numero : ITEMS_PER_PAGE_OPTIONS_SERVE_SIDE[0];
    },

    /** As opções já são as aplicadas? (evita reassinalar o objeto a cada ciclo) */
    mesmasOpcoes: function (proximas) {
      const atuais = this.opcoes || {};

      return Number(atuais.page) === Number(proximas.page)
        && Number(atuais.itemsPerPage) === Number(proximas.itemsPerPage)
        && JSON.stringify(atuais.sortBy || []) === JSON.stringify(proximas.sortBy || [])
        && JSON.stringify(atuais.sortDesc || []) === JSON.stringify(proximas.sortDesc || []);
    },

    /**
     * A página/tamanho/ordenação pedidos diferem do que está carregado? Tudo é comparado como
     * **número**: um `meta.current_page` textual faria a tela entrar em loop de chamadas.
     */
    mudouBusca: function (pagina, porPagina, sort) {
      return !this.ultimaAssinatura
        || Number(pagina) !== Number(this.pagina)
        || Number(porPagina) !== Number(this.porPagina)
        || JSON.stringify(sort) !== JSON.stringify(this.ordenacao);
    },

    /** Alinhamento do conteúdo do cabeçalho (o ícone fica sempre à direita do texto). */
    estiloCabecalho: function (head) {
      let alinhamento = "flex-start";

      if (head.align === "end") alinhamento = "flex-end";
      else if (head.align === "center") alinhamento = "center";

      return { justifyContent: alinhamento };
    },

    /** A coluna está ordenada? (usado pelo cabeçalho customizado) */
    colunaOrdenada: function (head, options) {
      return (options.sortBy || []).indexOf(head.value) >= 0;
    },

    /** A ordenação da coluna é descendente? */
    colunaDescendente: function (head, options) {
      const indice = (options.sortBy || []).indexOf(head.value);

      return indice >= 0 && !!(options.sortDesc || [])[indice];
    },

    /** Percentual formatado (1 casa, com vírgula). */
    percentual: function (valor, total) {
      if (!total) return "0,0%";

      return `${(valor / total * 100).toFixed(1).replace(".", ",")}%`;
    },

    /** Horas formatadas (1 casa, com vírgula). */
    formatarHoras: function (valor) {
      return Number(valor || 0).toFixed(1).replace(".", ",");
    },

    /** Rótulo da fase na abertura "Alocação por projeto e fase". */
    rotuloFase: function (fase) {
      return ROTULO_FASE[fase] || "Sem fase";
    },

  },
};
</script>

<template>
  <div class="content">
    <!-- Cabeçalho da tela -->
    <div class="header px-3 pt-3">
      <h3 class="h3 bold mb-0">Apuração por Colaborador</h3>
    </div>


    <!-- Cartões de resumo -->
    <div class="d-flex flex-wrap mt-3 mb-3">
      <v-card
        v-for="cartao in cartoes"
        :key="cartao.chave"
        :class="['cartao-resumo', cartao.classe]"
        elevation="0"
      >
        <div class="cartao-titulo">{{ cartao.titulo }}</div>
        <div class="cartao-valor">{{ cartao.valor | toCurrency }}</div>
        <div v-if="cartao.percentual" class="cartao-percentual">
          {{ cartao.percentual }} do custo do mês
        </div>
        <div v-else class="cartao-percentual">&nbsp;</div>
        <div class="cartao-secundario">{{ cartao.secundario }}</div>
      </v-card>
    </div>

    <v-card :color="tableColor" elevation="0">
      <v-card-text class="barra-acoes d-flex flex-wrap align-center">
        <!-- Atualizar -->
        <div
          class="table-v-action-button mr-2 alinhar-campo actionBarButtons"
          @click="carregar({ page: 1 })"
        >
          <v-icon>mdi-refresh</v-icon>
          Atualizar
        </div>

        <AsyncExportButton
          class="mr-2 alinhar-campo"
          aria-label="exportacaoAssincrona"
          type="capex-apuracao-colaboradores"
          :ano-base="filtroAno"
          :beneficio="filtroBeneficio"
          :competencia-unica="competenciaApi"
        >
          <v-icon>mdi-download-outline</v-icon> Exportar
        </AsyncExportButton>

        <!-- Filtros (label acima do campo) -->
        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-ano">Ano-base</label>
          <v-select
            id="filtro-ano"
            v-model="filtroAno"
            :items="anosOptions"
            class="table-v-action-button"
            dense
            hide-details
            outlined
            style="max-width: 120px"
            @change="trocarAno"
          ></v-select>
        </div>

        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-competencia">Competência</label>
          <div class="d-flex align-center">
            <v-btn small icon :disabled="ehPrimeiraCompetencia" @click="mudarCompetencia(-1)">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-text-field
              id="filtro-competencia"
              v-model="filtroCompetencia"
              class="table-v-action-button mx-1"
              dense
              hide-details
              outlined
              readonly
              style="max-width: 110px"
            ></v-text-field>
            <v-btn small icon :disabled="ehUltimaCompetencia" @click="mudarCompetencia(1)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-beneficio">Benefício</label>
          <v-select
            id="filtro-beneficio"
            v-model="filtroBeneficio"
            :items="beneficios"
            class="table-v-action-button"
            dense
            hide-details
            outlined
            style="max-width: 190px"
          ></v-select>
        </div>

        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-colaborador">Buscar colaborador</label>
          <v-text-field
            id="filtro-colaborador"
            v-model="filtroColaborador"
            class="table-v-action-button"
            dense
            hide-details
            placeholder="Nome do colaborador"
            outlined
            style="max-width: 210px"
          ></v-text-field>
        </div>
      </v-card-text>

      <v-data-table
        id="table_v"
        :headers="headers"
        :items="rows"
        :loading="loading"
        loading-text="Buscando dados..."
        no-data-text="Não existem dados para mostrar."
        height="calc(100vh - 421px)"
        item-key="id"
        item-class="table-v-tr"
        show-expand
        single-expand
        :expanded.sync="expandidos"
        hide-default-header
        :options="opcoes"
        :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
        :server-items-length="totalRegistros"
        :mobile-breakpoint="0"
        @update:options="onChangePage"
      >
      <template v-slot:header="{ props: { headers, options }, on: { sort } }">
        <thead class="v-data-table-header">
          <tr>
            <th
              v-for="head in headers"
              :key="head.value"
              :class="[
                `text-${head.align || 'start'}`,
                {
                  sortable: head.sortable !== false,
                  active: colunaOrdenada(head, options),
                  asc: !colunaDescendente(head, options),
                  desc: colunaDescendente(head, options),
                  nowrap: head.nowrapHeader !== false
                }
              ]"
              :data-label="head.text"
              :width="head.value === 'data-table-expand' ? '48px' : head.width"
              :style="{ minWidth: head.width, maxWidth: head.width }"
              role="columnheader"
              scope="col"
            >
              <template v-if="head.value === 'data-table-expand'">
                <span></span>
              </template>

              <template v-else>
                <div class="th-conteudo" :style="estiloCabecalho(head)">
                  <span
                    class="th-texto"
                    :class="{ 'clamp-duas-linhas': head.nowrapHeader === false }"
                    @click="head.sortable !== false && sort(head.value)"
                  >
                    {{ head.text }}
                  </span>
                  <i
                    v-if="head.sortable !== false"
                    aria-hidden="true"
                    class="v-icon notranslate v-data-table-header__icon mdi mdi-chevron-down theme--light"
                    style="font-size: 18px"
                    @click="sort(head.value)"
                  ></i>
                </div>
              </template>
            </th>
          </tr>
        </thead>
      </template>

      <template #[`item.data-table-expand`]="{ item, isExpanded, expand }">
        <v-btn
          icon
          small
          :aria-label="`expandir-${item.id}`"
          @click.stop="alternarDetalhe(item, isExpanded, expand, $event)"
        >
          <v-icon size="22">{{ isExpanded ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        </v-btn>
      </template>

      <!-- Colaborador -->
      <template #[`item.colaborador`]="{ item }">
        <span class="clamp-duas-linhas" :title="item.colaborador">{{ item.colaborador }}</span>
      </template>

      <!-- Horas trabalhadas -->
      <template #[`item.horas`]="{ item }">
        {{ formatarHoras(item.horas) }} h
      </template>

      <!-- Custo do mês -->
      <template #[`item.custo`]="{ item }">
        {{ item.custo | toCurrency }}
      </template>

      <!-- Pesquisa -->
      <template #[`item.custoPesquisa`]="{ item }">
        <div class="d-flex flex-column align-end">
          <span>{{ item.custoPesquisa | toCurrency }}</span>
          <small class="percentual">{{ percentual(item.custoPesquisa, item.custo) }}</small>
        </div>
      </template>

      <!-- Desenvolvimento -->
      <template #[`item.custoDesenvolvimento`]="{ item }">
        <div class="d-flex flex-column align-end">
          <span>{{ item.custoDesenvolvimento | toCurrency }}</span>
          <small class="percentual">{{ percentual(item.custoDesenvolvimento, item.custo) }}</small>
        </div>
      </template>

      <!-- Sem fase -->
      <template #[`item.custoSemFase`]="{ item }">
        <div class="d-flex flex-column align-end">
          <span>{{ item.custoSemFase | toCurrency }}</span>
          <small class="percentual">{{ percentual(item.custoSemFase, item.custo) }}</small>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-0">
          <div class="abertura-grupos">
            <div v-if="item.carregandoDetalhe" class="abertura-titulo">Carregando a folha do mês...</div>

            <template v-if="item.detalhe">
              <div class="abertura-titulo">Folha do mês por rubrica</div>
              <div v-for="rubrica in item.detalhe.rubricas" :key="rubrica.id" class="linha-rubrica">
                <span>{{ rubrica.nome }}</span>
                <div class="text-end">{{ rubrica.valor | toCurrency }}</div>
                <div class="text-end">
                  <div class="d-flex flex-column align-end">
                    <span>{{ rubrica.pesquisa | toCurrency }}</span>
                    <small class="percentual">{{ percentual(rubrica.pesquisa, rubrica.valor) }}</small>
                  </div>
                </div>
                <div class="text-end">
                  <div class="d-flex flex-column align-end">
                    <span>{{ rubrica.desenvolvimento | toCurrency }}</span>
                    <small class="percentual">{{ percentual(rubrica.desenvolvimento, rubrica.valor) }}</small>
                  </div>
                </div>
                <div class="text-end">
                  <div class="d-flex flex-column align-end">
                    <span>{{ rubrica.semFase | toCurrency }}</span>
                    <small class="percentual">{{ percentual(rubrica.semFase, rubrica.valor) }}</small>
                  </div>
                </div>
              </div>

              <div class="abertura-titulo mt-2">Alocação por projeto e fase (proporção das horas)</div>
              <div
                v-for="alocacao in item.detalhe.alocacoes"
                :key="alocacao.id"
                class="linha-alocacao"
              >
                <span :title="alocacao.projeto">{{ alocacao.projeto }}</span>
                <div>{{ rotuloFase(alocacao.fase) }}</div>
                <div class="text-end">{{ formatarHoras(alocacao.horas) }} h</div>
                <div class="text-end">{{ alocacao.valor | toCurrency }}</div>
              </div>
            </template>
          </div>
        </td>
      </template>

      <template v-slot:body.append>
        <tr v-if="temDados" class="linha-total">
          <td></td>
          <td>{{ textoTotalGeral }}</td>
          <td>
            <div class="d-flex flex-column align-end">{{ formatarHoras(totais.horas) }} h</div>
          </td>
          <td>
            <div class="d-flex flex-column align-end">
              {{ totais.custo | toCurrency }}
            </div>
          </td>
          <td>
            <div class="d-flex flex-column align-end">
              <span>{{ totais.custoPesquisa | toCurrency }}</span>
              <small class="percentual">{{ percentual(totais.custoPesquisa, totais.custo) }}</small>
            </div>
          </td>
          <td>
            <div class="d-flex flex-column align-end">
              <span>{{ totais.custoDesenvolvimento | toCurrency }}</span>
              <small class="percentual">{{ percentual(totais.custoDesenvolvimento, totais.custo) }}</small>
            </div>
          </td>
          <td>
            <div class="d-flex flex-column align-end">
              <span>{{ totais.custoSemFase | toCurrency }}</span>
              <small class="percentual">{{ percentual(totais.custoSemFase, totais.custo) }}</small>
            </div>
          </td>
        </tr>
      </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<style lang="scss" scoped>
.barra-acoes {
  padding: 0.5rem 1rem;
}

.filtro-label {
  display: block;
  margin: 0 0 2px 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.2;
  color: #3e3e3f;
}

.alinhar-campo {
  margin-top: 15px;
}

.cartao-resumo {
  flex: 1 1 12.5rem;
  min-width: 12.5rem;
  margin-right: 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 0.25rem solid #757575; /* Sem fase: cinza um pouco mais escuro. */
}

.cartao-titulo {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #757575;
}

.cartao-valor {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
  color: #1c1c44;
}

.cartao-percentual,
.cartao-secundario {
  font-size: 0.75rem;
  color: #6b6b6b;
}

.cartao-total {
  border-left-color: #1c1c44;

  .cartao-titulo {
    color: #1c1c44;
  }
}

.cartao-pesquisa {
  border-left-color: #1565c0;

  .cartao-titulo {
    color: #1565c0;
  }
}

.cartao-desenvolvimento {
  border-left-color: #5e35b1;

  .cartao-titulo {
    color: #5e35b1;
  }
}

.cartao-sem-fase {
  border-left-color: #757575;

  .cartao-titulo {
    color: #757575;
  }
}

.clamp-duas-linhas {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
}

/* Layout automático com piso por coluna: a coluna cresce se o conteúdo precisar. */
::v-deep .v-data-table__wrapper table {
  table-layout: auto;
  /* Abaixo deste piso as colunas se espremem e a tabela rola na horizontal. */
  min-width: 1100px;
}

/* Valores e horas não quebram: a coluna cresce. Vale só para o corpo. */
::v-deep td.text-end,
::v-deep td .text-end {
  white-space: nowrap;
}

::v-deep thead.v-data-table-header > tr {
  border-bottom: 0 !important;
  position: sticky;
  top: 0;
  z-index: 2;
}

.th-conteudo {
  display: flex;
  align-items: center;
  width: 100%;
}

.th-texto {
  min-width: 0;
}

/* Ícone de ordenação sempre visível (o Vuetify só mostra no hover/coluna ativa).
   No hover ele herda a cor do cabeçalho, igual à coluna em uso. */
.th-conteudo .v-data-table-header__icon {
  flex: 0 0 auto;
  opacity: 1;
}

th.sortable:hover .th-conteudo .v-data-table-header__icon,
th.active .th-conteudo .v-data-table-header__icon {
  color: inherit;
}

.abertura-grupos {
  background-color: #f5f5f5;
}

.abertura-titulo {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #3e3e3f;
  padding: 0.5rem 0.5rem 0 3.5rem;
}

.linha-rubrica,
.linha-alocacao {
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem 0.25rem 3.5rem;

  &:last-child {
    border-bottom: 0;
  }

  > * {
    min-width: 0;
  }
}

.linha-rubrica > * {
  &:first-child {
    flex: 1 1 auto;
    color: #3e3e3f;
  }

  &:nth-child(2) {
    flex: 0 0 9.375rem;
  }

  &:nth-child(3) {
    flex: 0 0 10.625rem;
  }

  &:nth-child(4) {
    flex: 0 0 11.25rem;
  }

  &:nth-child(5) {
    flex: 0 0 10.625rem;
  }
}

.linha-alocacao > * {
  &:first-child {
    flex: 1 1 auto;
    color: #3e3e3f;
  }

  &:nth-child(2) {
    flex: 0 0 9.375rem;
    color: #6b6b6b;
  }

  &:nth-child(3) {
    flex: 0 0 6.875rem;
  }

  &:nth-child(4) {
    flex: 0 0 10.625rem;
  }
}

.percentual {
  color: #6b6b6b;
}

::v-deep .v-data-table {
  border-radius: 0 !important;
}

.linha-total td {
  background-color: #dfeffe !important;
  background-color: color-mix(in srgb, var(--v-table-header-base) 45%, #ffffff) !important;
  border-top: 2px solid rgba(0, 0, 0, 0.12);
  font-weight: 700;
  color: #1c1c44;
  position: sticky;
  bottom: 0;
  z-index: 1;
}
</style>
